import { useState, useEffect } from 'react';

export interface ConsentPreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

export interface ConsentState {
  hasConsented: boolean;
  preferences: ConsentPreferences;
  consentDate: string | null;
}

const DEFAULT_PREFERENCES: ConsentPreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
};

export const useConsent = () => {
  const [consentState, setConsentState] = useState<ConsentState>({
    hasConsented: false,
    preferences: DEFAULT_PREFERENCES,
    consentDate: null,
  });

  // Load consent from localStorage on mount
  useEffect(() => {
    try {
      const savedConsent = localStorage.getItem('cookieConsent');
      const savedDate = localStorage.getItem('cookieConsentDate');
      const hasExplicitConsent = localStorage.getItem('cookieConsentExplicit');
      
      if (savedConsent && savedDate && hasExplicitConsent) {
        const preferences = JSON.parse(savedConsent);
        setConsentState({
          hasConsented: true,
          preferences,
          consentDate: savedDate,
        });
      }
    } catch (error) {
      console.error('Error loading consent preferences:', error);
      // Clear corrupted data
      localStorage.removeItem('cookieConsent');
      localStorage.removeItem('cookieConsentDate');
      localStorage.removeItem('cookieConsentExplicit');
    }
  }, []);

  const updateConsent = (preferences: ConsentPreferences) => {
    const consentDate = new Date().toISOString();
    
    // Save to localStorage with explicit consent flag
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    localStorage.setItem('cookieConsentDate', consentDate);
    localStorage.setItem('cookieConsentExplicit', 'true');
    
    // Update state
    setConsentState({
      hasConsented: true,
      preferences,
      consentDate,
    });

    // Trigger analytics initialization based on consent
    if (preferences.analytics) {
      initializeAnalytics();
    }

    if (preferences.marketing) {
      initializeMarketingTracking();
    }
  };

  const clearConsent = () => {
    localStorage.removeItem('cookieConsent');
    localStorage.removeItem('cookieConsentDate');
    localStorage.removeItem('cookieConsentExplicit');
    setConsentState({
      hasConsented: false,
      preferences: DEFAULT_PREFERENCES,
      consentDate: null,
    });
  };

  const isConsentExpired = (months: number = 12): boolean => {
    if (!consentState.consentDate) return false;
    
    const consentDate = new Date(consentState.consentDate);
    const expiryDate = new Date(consentDate);
    expiryDate.setMonth(expiryDate.getMonth() + months);
    
    return new Date() > expiryDate;
  };

  // Helper functions to check specific consent types
  const hasAnalyticsConsent = () => consentState.hasConsented && consentState.preferences.analytics;
  const hasMarketingConsent = () => consentState.hasConsented && consentState.preferences.marketing;

  return {
    consentState,
    updateConsent,
    clearConsent,
    isConsentExpired,
    hasAnalyticsConsent,
    hasMarketingConsent,
  };
};

// Placeholder functions for analytics initialization
// These will be implemented when we add GA4 and LinkedIn tracking
const initializeAnalytics = () => {
  console.log('Analytics consent granted - GA4 would be initialized here');
  // TODO: Initialize Google Analytics 4
  // window.gtag('consent', 'update', { analytics_storage: 'granted' });
};

const initializeMarketingTracking = () => {
  console.log('Marketing consent granted - LinkedIn Insight would be initialized here');
  // TODO: Initialize LinkedIn Insight Tag
  // window._linkedin_partner_id = "YOUR_PARTNER_ID";
};

export default useConsent;